// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseModalComponent, BaseModalConfig } from '@lfx/shared/components/base-modal';
import { spaceValidator } from '@lfx/shared/validators';
import { KeyProjectsService } from '@lfx/features/key-projects/services/key-projects.service';
import { DatadogErrorTrackingService } from '@lfx/core/services';

@Component({
  selector: 'lfx-add-workspace-modal',
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent],
  template: `
    <lfx-base-modal
      [config]="modalConfig"
      [isLoading]="isLoading"
      [errorMessage]="errorMessage"
      (confirmed)="onSubmit()"
      (cancelled)="onCancel()">

      <div class="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-mb-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg">
        <i class="fas fa-info-circle tw-text-gray-400 tw-mt-1"></i>
        <span class="tw-text-sm tw-text-gray-600">This will be shared across all users in your company.</span>
      </div>

      <form [formGroup]="addWorkspaceForm" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <input
            type="text"
            id="workspaceName"
            class="form-control lf-form-control-large"
            [class.is-invalid]="!!getControlErrors(workspaceNameControl)"
            formControlName="workspaceName"
            placeholder="Enter workspace name"
            [disabled]="isLoading"
          />
          <!-- Validation Errors -->
          <ng-container *ngIf="getControlErrors(workspaceNameControl) as errors">
            <div class="invalid-feedback" *ngIf="errors['required'] || errors['spaceError']">
              Workspace name is required.
            </div>
            <div class="invalid-feedback" *ngIf="errors['workspaceExists']">
              Workspace name already exists.
            </div>
          </ng-container>
        </div>
      </form>
    </lfx-base-modal>
  `,
  styleUrls: ['./add-workspace-modal.component.scss']
})
export class AddWorkspaceModalComponent implements OnInit, OnDestroy {
  @ViewChild(BaseModalComponent) baseModal!: BaseModalComponent;

  addWorkspaceForm!: FormGroup<{
    workspaceName: FormControl<string | null>;
  }>;

  isLoading = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();

  modalConfig: BaseModalConfig = {
    title: 'Add workspace',
    confirmLabel: 'Add workspace',
    cancelLabel: 'Cancel',
    confirmVariant: 'primary',
    allowBackdropClose: false
  };

  private readonly errorTracking = inject(DatadogErrorTrackingService);

  constructor(
    private formBuilder: FormBuilder,
    private keyProjectsService: KeyProjectsService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Custom validator to check if workspace name already exists
   */
  private workspaceNameExistsValidator(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const workspaceName = control.value.trim().toLowerCase();
      const existingNames = this.keyProjectsService.workspaces().map(workspace => workspace.name.toLowerCase());

      if (existingNames.includes(workspaceName)) {
        return { 'workspaceExists': { value: control.value } };
      }

      return null;
    };
  }

  private initializeForm(): void {
    this.addWorkspaceForm = this.formBuilder.group({
      workspaceName: ['', [Validators.required, spaceValidator, this.workspaceNameExistsValidator()]]
    });

    // Update modal config based on form validity
    this.addWorkspaceForm.statusChanges.subscribe(status => {
      this.modalConfig = {
        ...this.modalConfig,
        confirmDisabled: status === 'INVALID'
      };
    });
  }

  get workspaceNameControl(): FormControl {
    return this.addWorkspaceForm.get('workspaceName') as FormControl;
  }

  getControlErrors(control: FormControl): any {
    return control.invalid && (control.dirty || control.touched) ? control.errors : null;
  }

  onCancel(): void {
    // Base modal handles the dismissal
  }

  onSubmit(): void {
    if (this.addWorkspaceForm.valid && !this.isLoading) {
      this.createWorkspace();
    } else if (this.addWorkspaceForm.invalid) {
      // Track validation errors when user attempts to submit invalid form
      this.errorTracking.trackValidationError({
        feature: 'key-projects',
        action: 'create-workspace',
        validationErrors: this.getValidationErrorKeys(),
        component: 'AddWorkspaceModalComponent',
      });
    }
  }

  /**
   * Get array of validation error keys for tracking
   */
  private getValidationErrorKeys(): string[] {
    const errors: string[] = [];
    const controlErrors = this.workspaceNameControl.errors;

    if (controlErrors) {
      if (controlErrors['required'] || controlErrors['spaceError']) {
        errors.push('workspaceName:required');
      }
      if (controlErrors['workspaceExists']) {
        errors.push('workspaceName:exists');
      }
    }

    return errors;
  }

  private createWorkspace(): void {
    const workspaceName = this.workspaceNameControl.value?.trim();
    if (!workspaceName) return;

    this.isLoading = true;
    this.errorMessage = '';

    this.keyProjectsService.createWorkspace(workspaceName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (workspace) => {
          this.baseModal.closeWithResult(workspace);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message || 'Failed to create workspace. Please try again.';
        }
      });
  }
}
